/**
 * Serialização dos exames modulares pra texto legível. Usado em três lugares:
 *
 *   - Prontuário (`PatientSessionPanel.handleSaveEncounterExams`) — vira o
 *     campo `content.text` da entrada `CONSULTATION`, pesquisável e copiável.
 *   - Contexto pra IA (`buildExtractionContext`) — injeta no prompt de
 *     extração pra a IA enxergar o que o médico já preencheu nos módulos.
 *   - Impressão / cópia rápida — qualquer lugar que precise de "snapshot
 *     em texto" do estado atual sem ir pro PDF.
 *
 * O formato é Markdown leve (## tipo, ### módulo, - campo: OD x | OE y).
 * Campos vazios são OMITIDOS — só sai o que o médico realmente preencheu.
 */

import { EXAM_MODULE_BY_ID, BUILTIN_EXAM_MODULES } from '@/data/encounterCatalog';
import type {
  EncounterExamsState,
  EncounterTypeDef,
  ExamModuleDef,
  ExamModuleEntry,
} from '@/types/encounter';

/**
 * Um OLHO de um módulo em texto, com os campos em SEQUÊNCIA — sem o prefixo
 * "OD"/"OE" (quem chama decide como rotular a linha).
 *
 * A leitura clínica é por olho ("OD esférico, cilíndrico, eixo"), não campo
 * a campo com os dois olhos dentro de cada um. `·` separa campos dentro do
 * olho; `|` fica reservado pra separar OLHOS em quem escreve inline.
 *
 * O rótulo do campo usa o `shortLabel` do catálogo quando existe (Esf./Cil./
 * Eixo) e SOME quando o módulo tem um só campo por olho (PIO, paquimetria…),
 * onde o nome do módulo já diz o que é o número.
 *
 * Devolve null quando o olho não tem nenhum valor preenchido.
 */
export function formatModuleEye(
  mod: ExamModuleDef,
  entry: ExamModuleEntry | undefined,
  eye: 'OD' | 'OE',
): string | null {
  const eyeFields = mod.fields.filter((f) => (f.layout ?? 'eyes') !== 'mono');
  const single = eyeFields.length === 1;
  const parts: string[] = [];
  for (const field of eyeFields) {
    const raw = (entry?.values[field.key] ?? {})[eye]?.trim();
    if (!raw) continue;
    const suf = field.suffix ? ` ${field.suffix}` : '';
    const name = single ? '' : `${field.shortLabel ?? field.label} `;
    parts.push(`${name}${raw}${suf}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Campos sem olho do módulo (ex.: Cicloplegia) — rótulo e valor separados,
 * porque cada consumidor os junta do seu jeito ("Rótulo: valor" no Markdown,
 * "Rótulo valor" no rail). Só os preenchidos.
 */
export function formatModuleMonoFields(
  mod: ExamModuleDef,
  entry: ExamModuleEntry | undefined,
): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [];
  for (const field of mod.fields) {
    if ((field.layout ?? 'eyes') !== 'mono') continue;
    const v = entry?.values[field.key]?.mono?.trim();
    if (!v) continue;
    const suf = field.suffix ? ` ${field.suffix}` : '';
    out.push({ label: field.label, value: `${v}${suf}` });
  }
  return out;
}

/**
 * Serializa um `EncounterExamsState` em Markdown. Retorna string vazia se
 * não houver nenhum campo preenchido.
 *
 * @param state           Estado dos exames modulares.
 * @param encounterTypes  Lista efetiva de tipos (builtins + custom). Usada
 *                        APENAS pra resolver o label de `state.typeId`.
 *                        Quando ausente, o label cai pra o próprio id.
 */
export function serializeEncounterExams(
  state: EncounterExamsState,
  encounterTypes?: EncounterTypeDef[],
  opts?: {
    /**
     * Agrupa por OLHO em vez de por campo: cada módulo vira uma linha inline
     * pro OD (todos os campos daquele olho) e outra pro OE — igual ao fluxo
     * da Receita de Óculos. Usado no "Salvar no prontuário" dos exames do
     * atendimento. Campos `mono` (ex.: Cicloplegia) saem em linha própria.
     * Default `false` → formato por campo (`- campo: OD x | OE y`), mantido
     * pro contexto da IA e demais consumidores.
     */
    groupByEye?: boolean;
  },
): string {
  if (!state.entries || state.entries.length === 0) return '';

  const lines: string[] = [];
  const typeLabel = state.typeId
    ? encounterTypes?.find((t) => t.id === state.typeId)?.label ?? state.typeId
    : undefined;
  if (typeLabel) lines.push(`## Tipo de atendimento: ${typeLabel}`, '');

  let anyFilled = false;
  for (const entry of state.entries) {
    const mod = EXAM_MODULE_BY_ID[entry.moduleId];
    if (!mod) continue;

    const filledFields = mod.fields.filter((f) => {
      const v = entry.values[f.key] ?? {};
      return v.OD?.trim() || v.OE?.trim() || v.mono?.trim();
    });
    if (filledFields.length === 0) continue;
    anyFilled = true;

    lines.push(`### ${mod.label}`);
    if (opts?.groupByEye) {
      // Agrupado por olho: uma linha pro OD com todos os campos daquele olho,
      // outra pro OE. Campos `mono` saem depois, em linha própria. Mesmas
      // funções que o rail "Última consulta" usa — um formato só.
      for (const eye of ['OD', 'OE'] as const) {
        const body = formatModuleEye(mod, entry, eye);
        if (body) lines.push(`- ${eye}: ${body}`);
      }
      for (const { label, value } of formatModuleMonoFields(mod, entry)) {
        lines.push(`- ${label}: ${value}`);
      }
    } else {
      for (const field of filledFields) {
        const v = entry.values[field.key] ?? {};
        const suf = field.suffix ? ` ${field.suffix}` : '';
        if ((field.layout ?? 'eyes') === 'mono') {
          if (v.mono?.trim()) lines.push(`- ${field.label}: ${v.mono.trim()}${suf}`);
        } else {
          const parts: string[] = [];
          if (v.OD?.trim()) parts.push(`OD ${v.OD.trim()}${suf}`);
          if (v.OE?.trim()) parts.push(`OE ${v.OE.trim()}${suf}`);
          if (parts.length) lines.push(`- ${field.label}: ${parts.join(' | ')}`);
        }
      }
    }
    lines.push('');
  }

  if (!anyFilled && !typeLabel) return '';
  return lines.join('\n').trim();
}

/**
 * Serializa o CATÁLOGO de módulos de exame pra IA preencher (caminho inverso
 * do serializeEncounterExams): lista id/label/hint de cada módulo e, por
 * campo, key/label/layout/sufixo/opções. A IA usa isso pra devolver as
 * medições mapeadas (moduleId → fieldKey → {OD?,OE?,mono?}).
 *
 * @param moduleIds  Restringe aos módulos do tipo de atendimento ativo (corta
 *                   tokens e reduz a IA inventar módulo fora da tela). Sem
 *                   lista → catálogo inteiro.
 *
 * EXCLUI a categoria 'diagnostico' (classificação/estadiamento/CID/diagnóstico):
 * não são medições, fazem carry-over entre consultas e alimentam laudos —
 * ficam FORA do preenchimento automático pela IA.
 */
export function serializeCatalogForAI(moduleIds?: string[]): string {
  const base: ExamModuleDef[] =
    moduleIds && moduleIds.length
      ? moduleIds.map((id) => EXAM_MODULE_BY_ID[id]).filter((m): m is ExamModuleDef => !!m)
      : BUILTIN_EXAM_MODULES;
  // Exclui diagnóstico e anamnese (não-medição — a anamnese é preenchida
  // pela ponte AIEvolucao do M2, não pelo prompt de medições) e módulos
  // deprecated (substituídos por unificados no M3) — a IA só preenche os
  // módulos ativos de medição. Os de histórico (PIO basal) o sistema preenche.
  const mods = base.filter(
    (m) =>
      m.category !== 'diagnostico' && m.category !== 'anamnese' && !m.deprecated && !m.fromHistory,
  );
  if (mods.length === 0) return '';

  const lines: string[] = [];
  for (const mod of mods) {
    const hint = mod.hint ? ` — ${mod.hint}` : '';
    lines.push(`• "${mod.id}" — ${mod.label}${hint}:`);
    for (const f of mod.fields) {
      const layout = (f.layout ?? 'eyes') === 'mono' ? 'valor único → "mono"' : 'por olho → "OD"/"OE"';
      const suffix = f.suffix ? `; em ${f.suffix}` : '';
      const opts =
        f.kind === 'select' && f.options?.length ? `; opções: ${f.options.join(' | ')}` : '';
      lines.push(`  - "${f.key}" (${f.label}${suffix}; ${layout}${opts})`);
    }
  }
  return lines.join('\n');
}
